import { useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, X, Download, Printer, Users } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonHandout({ onClose }) {
  const [topic, setTopic] = useState('');
  const [scripture, setScripture] = useState('');
  const [ageGroup, setAgeGroup] = useState('adults');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/tools/lesson-handout`, {
        topic: topic.trim(), age_group: ageGroup, scripture: scripture.trim()
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate handout');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${topic} Handout</title><style>body{font-family:Georgia,serif;max-width:700px;margin:40px auto;line-height:1.8;} h1,h2,h3{color:#1B3A6B;} .blank{display:inline-block;min-width:120px;border-bottom:2px solid #333;}</style></head><body>`);
    win.document.write(document.querySelector('[data-testid="handout-content"]')?.innerHTML || '');
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="handout-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-[#1B3A6B]">Lesson Handout Generator</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="handout-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          <p className="text-sm text-[#475569] mb-3">Generate printable fill-in-the-blank handouts for Sunday School, Bible Study, or small groups.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input
              value={topic}
              onChange={e => setTopic(e.target.value)}
              placeholder="Lesson topic (e.g., The Good Samaritan)"
              className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
              data-testid="handout-topic-input"
            />
            <input
              value={scripture}
              onChange={e => setScripture(e.target.value)}
              placeholder="Scripture reference (optional)"
              className="px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
              data-testid="handout-scripture-input"
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex bg-[#F4F2EC] rounded-lg p-0.5">
              {[
                { key: 'kids', label: 'Kids', icon: '5-10' },
                { key: 'teens', label: 'Teens', icon: '11-17' },
                { key: 'adults', label: 'Adults', icon: '18+' },
              ].map(a => (
                <button
                  key={a.key}
                  onClick={() => setAgeGroup(a.key)}
                  className={`px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                    ageGroup === a.key ? 'bg-[#1B3A6B] text-white shadow-sm' : 'text-[#475569] hover:text-[#0F1A2A]'
                  }`}
                  data-testid={`handout-age-${a.key}`}
                >
                  {a.label} ({a.icon})
                </button>
              ))}
            </div>
            <button
              onClick={generate}
              disabled={loading || !topic.trim()}
              className="ml-auto px-5 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 hover:bg-[#254B8A]"
              data-testid="handout-generate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Generate
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {error && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">{error}</div>}
          {loading && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#1B3A6B] mx-auto mb-3" />
              <p className="text-sm text-[#475569]">Generating {ageGroup} handout for "{topic}"...</p>
            </div>
          )}
          {result && (
            <div>
              <div className="flex gap-2 mb-4">
                <span className="px-3 py-1 bg-[#1B3A6B]/10 text-[#1B3A6B] rounded-full text-xs font-medium capitalize">{result.age_group}</span>
                <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1 border border-[#E2DFD8] rounded-full text-xs text-[#475569] hover:bg-[#F4F2EC]">
                  <Printer className="w-3 h-3" /> Print
                </button>
              </div>
              <div className="preview-content prose prose-sm max-w-none" data-testid="handout-content">
                <ReactMarkdown>{result.handout}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
